// External
import { z, type TypeOf } from 'zod';
import { buildResponse } from '@schemas/services';

export const LoginResponseSchema = z.object({
  token_type: z.string(),
  access_token: z.string(),
  expires_in: z.number(),
  refresh_token: z.string(),
});
export type LoginResponse = TypeOf<typeof LoginResponseSchema>;

export const UserDataSchema = z.object({
  userSub: z.string(),
  userName: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  givenName: z.string().optional().nullable(),
  familyName: z.string().optional().nullable(),
  featureAttributes: z.array(z.string()).optional().nullable(),
  vesselImos: z.array(z.number()).nullable(),
  clientId: z.number().optional().nullable(),
  clientName: z.string().optional().nullable(),
  clientLogo: z.string().optional().nullable(),
  enabled: z.boolean(),
  creationDate: z.string().datetime(),
  lastModifiedDate: z.string().datetime(),
});
export type UserData = TypeOf<typeof UserDataSchema>;

// Schema for the EEOI fleet scores response data
export const EeoiVesselSchema = z
  .object({
    imo: z.number(),
    vesselName: z.string(),
    vesselType: z.string(),
    vesselTypeId: z.number(),
    shipyard: z.string(),
    yearBuilt: z.number(),
    lastNoonReportDate: z.string(),
    deadWeightTonnage: z.number(),
    cargo: z.number(),
    cargoTimesDistance: z.number(),
    voyages: z.number(),
    distanceSailed: z.number(),
    foc: z.number(),
    co2: z.number(),
    totalDistance: z.number(),
    eeoiScore: z.number(),
    ch4: z.number(),
    n2o: z.number(),
    co2e: z.number(),
    wellToTank: z.number(),
    wellToWake: z.number(),
  })
  .strict();

export type EeoiVessel = TypeOf<typeof EeoiVesselSchema>;

export const EeoiFleetSchema = z.object({
  fleet: z.array(EeoiVesselSchema),
  availableYears: z.array(z.number()),
});
export type EeoiFleet = TypeOf<typeof EeoiFleetSchema>;

export const VesselsNamesSchema = z.array(
  z.object({
    vesselImo: z.number(),
    vesselName: z.string(),
  })
);

export type VesselsNames = TypeOf<typeof VesselsNamesSchema>;

export const VoyageSchema = z.object({
  voyageId: z.string(),
  originPort: z.string(),
  destinationPort: z.string(),
  timeDeparture: z.string(),
  timeArrival: z.string(),
  distanceTraveled: z.number(),
  averageSpeed: z.number(),
  fuelOilConsumption: z.number(),
  averageBeaufortScale: z.number(),
  co2Emissions: z.number(),
  ciiScore: z.number().nullable(),
  ciiRating: z.string().nullable(),
});
export const VoyagesSchema = z.array(VoyageSchema);
export const VoyagesResponseSchema = buildResponse<TypeOf<typeof VoyagesSchema>>(VoyagesSchema);

export type Voyages = TypeOf<typeof VoyagesSchema>;

export const PerformanceFleetSchema = z.array(
  z.object({
    imo: z.number().or(z.string()),
    vesselName: z.string(),
    vesselType: z.string(),
    vesselTypeId: z.number(),
    shipyard: z.string(),
    yearBuilt: z.number(),
    deadWeightTonnage: z.number(),
    department: z.string(),
    segment: z.string(),
    registeredOwner: z.string(),
    status: z.string(),
    lastDrydock: z.string().nullable().optional(),
    lastUnderwaterCleaning: z.string().nullable().optional(),
    addedResistance: z.number().nullable().optional(),
  })
);
export const PerformanceFleetResponseSchema =
  buildResponse<TypeOf<typeof PerformanceFleetSchema>>(PerformanceFleetSchema);
export type PerformanceFleet = TypeOf<typeof PerformanceFleetResponseSchema>;

const SpeedConsumptionCurveSchema = z.object({
  factor: z.string(),
  values: z.array(z.object({ speed: z.number(), foc: z.number() })),
});

export const FactorSchema = z.object({
  factor: z.string(),
  value: z.number(),
  units: z.string().optional(),
});

export const ResistanceSchema = z.object({
  timestamp: z.string(),
  resistance: z.number(),
});
export type Resistance = TypeOf<typeof ResistanceSchema>;

export const VesselPerformanceSchema = z.object({
  vesselName: z.string(),
  dataLastUpdated: z.string(),
  speedConsumptionCurves: z.array(SpeedConsumptionCurveSchema),
  focContributionFactors: z.object({
    yearToDate: z.array(FactorSchema),
    allTime: z.array(FactorSchema),
    sinceLastDrydock: z.array(FactorSchema),
    past12Months: z.array(FactorSchema),
    sinceLastUnderwaterCleaning: z.array(FactorSchema),
  }),
  dailyAverages: z.object({
    yearToDate: z.array(FactorSchema),
    allTime: z.array(FactorSchema),
    sinceLastDrydock: z.array(FactorSchema),
    past12Months: z.array(FactorSchema),
    sinceLastUnderwaterCleaning: z.array(FactorSchema),
  }),
  dryDockEvents: z.array(ResistanceSchema),
  underwaterCleaningEvents: z.array(ResistanceSchema),
  addedResistanceTrend: z.object({
    yearToDate: z.array(ResistanceSchema),
    allTime: z.array(ResistanceSchema),
    sinceLastDrydock: z.array(ResistanceSchema),
    past12Months: z.array(ResistanceSchema),
    sinceLastUnderwaterCleaning: z.array(ResistanceSchema),
  }),
});
export type VesselPerformance = TypeOf<typeof VesselPerformanceSchema>;

export const DailyFocContributionFactorSchema = z.object({
  numberDay: z.number(),
  day: z.string(),
  seaTemperature: z.number(),
  current: z.number(),
  wind: z.number(),
  wave: z.number(),
  fouling: z.number(),
  speed: z.number(),
});

export const PerformanceVoyageSchema = z.object({
  originPort: z.string(),
  destinationPort: z.string(),
  dataLastUpdated: z.string(),
  addedResistanceAverage: z.number(),
  focContributionFactorSums: z.array(FactorSchema),
  focContributionFactors: z.array(FactorSchema),
  dailyAverages: z.array(FactorSchema),
  dailyFocContributionFactors: z.array(DailyFocContributionFactorSchema),
});

export type PerformanceVoyage = TypeOf<typeof PerformanceVoyageSchema>;

export const GetFleetRatingsParamsSchema = z.object({
  /**
   * Time offset of the user that triggers the request
   */
  clientTimezoneOffset: z.number(),
  /**
   * Optional flag to send all the data needed for the export file
   */
  isDataExport: z.boolean().optional(),
  /**
   * Optional flag to send the export data in optimized way
   */
  optimized: z.boolean().optional(),
  /**
   * Optional flag to skip caching.
   */
  skipCache: z.boolean().optional(),
  /**
   * The initial Date.
   */
  startDate: z.date().optional(),
  /**
   * The end Date.
   */
  endDate: z.date().optional(),
});
export type GetFleetRatingsParams = TypeOf<typeof GetFleetRatingsParamsSchema>;

const Co2CorrectionFactorsSchema = z.object({
  boiler: z.number(),
  electrical: z.number(),
  others: z.number(),
  tf: z.number(),
  voyage: z.number(),
});

const PeriodFocByFuelTypeSchema = z.record(z.number());
export type PeriodFocByFuelType = TypeOf<typeof PeriodFocByFuelTypeSchema>;

const FocByFuelTypeSchema = z.record(z.array(z.number().nullable()));
export type FocByFuelType = TypeOf<typeof FocByFuelTypeSchema>;

const PeriodReportDataSchema = z.object({
  averageSpeeds: z.array(z.number()),
  co2: z.array(z.number()),
  distancesSailed: z.array(z.number()),
  foc: z.array(z.number()),
  focByFuelType: FocByFuelTypeSchema,
  reportDates: z.array(z.number()),
});
export type PeriodReportData = TypeOf<typeof PeriodReportDataSchema>;

const VesselCiiRatingsSchema = z.object({
  department: z.string(),
  endOfPeriodCiiRating: z.string().nullable(),
  endOfPeriodCiiScore: z.number().nullable(),
  endOfPeriodCo2: z.number().optional(),
  endOfPeriodCo2CorrectionFactors: Co2CorrectionFactorsSchema.optional(),
  endOfPeriodDistanceSailed: z.number().optional(),
  endOfPeriodFoc: z.number().optional(),
  endOfPeriodRequiredCiiScore: z.number().nullable(),
  forecastedCiiRating: z.string().nullable(),
  forecastedCiiScore: z.number().nullable(),
  forecastedCo2: z.number().nullable(),
  forecastedDistanceSailed: z.number().nullable(),
  forecastedFoc: z.number().nullable(),
  forecastedRequiredCiiScore: z.number().nullable(),
  imo: z.number(),
  lastNoonReportDate: z.string().datetime(),
  maxCapacity: z.number(),
  maxCapacityUnit: z.string(),
  periodAverageSpeed: z.number(),
  periodCiiRating: z.string().nullable(),
  periodCiiScore: z.number().nullable(),
  periodCo2: z.number(),
  periodCo2CorrectionFactors: Co2CorrectionFactorsSchema.optional(),
  periodDistanceSailed: z.number(),
  periodFoc: z.number(),
  periodFocByFuelType: PeriodFocByFuelTypeSchema.optional(),
  periodReportData: PeriodReportDataSchema.optional(),
  periodRequiredCiiScore: z.number().nullable(),
  segment: z.string(),
  shipyard: z.string(),
  startOfPeriodCiiRating: z.string().nullable(),
  startOfPeriodCiiScore: z.number().nullable(),
  startOfPeriodRequiredCiiScore: z.number().nullable(),
  vesselName: z.string(),
  vesselType: z.string(),
  vesselTypeId: z.number(),
  yearBuilt: z.number(),
});
export type VesselCiiRatings = TypeOf<typeof VesselCiiRatingsSchema>;

export const CiiFleetRatingsSchema = z.object({
  availableDateRange: z.object({
    maxDate: z.string().datetime(),
    minDate: z.string().datetime(),
  }),
  fleet: z.array(VesselCiiRatingsSchema),
});
export type CiiFleetRatings = TypeOf<typeof CiiFleetRatingsSchema>;
